import Backbone from 'backbone';
import $ from 'jquery';
import _ from 'underscore';
import Analytics from './analytics';
import { HUB_EVENTS } from './engine/arcade/arcade.types';
import Environment from './environment';
import GameFactory from './gameFactory';
import { getLogger } from './logging/logger';
import AudioManager from './manager/audioManager';
import InputManager from './manager/inputManager';
import PopUpManager from './manager/popUpManager';
import Android from './platform/android';
import Comcast from './platform/comcast';
import gsap from './util/hwpTVGSAP';

/**
 * @typedef {import('./engine/arcade/arcade.types').PageOptions} PageOptions
 */

const logger = getLogger('Router');

const getAnchorInDescendentTree = (node) => {
	logger.debug(`[${getAnchorInDescendentTree.name}]`, { node });
	while (node && node.nodeName !== 'BODY') {
		if (node.nodeName === 'A') return node;
		node = node.parentNode;
	}
	return undefined;
};

class Router extends Backbone.Router {
	constructor(options) {
		super(options);
		logger.log(`[${this.constructor.name}]`);

		/** @type {function[]}  An array of functions representing custom back navigation behaviors. */
		this.backStack = [];

		/** @type {string} The document selector used when displaying pages into the dom. */
		this.selector = '#page';
	}

	initialize(options) {
		logger.log(`[${this.initialize.name}]`, { options });

		// Fix for cross sub-domain issues with Backbone (ie api.tv.hollywoodplayer.com to tv.hollywoodplayer.com)
		(() => {
			const backboneSync = Backbone.sync;
			Backbone.sync = function proxySync(method, model, syncOptions) {
				logger.debug('[Proxied Sync]', { method, model, syncOptions });
				return backboneSync(method, model, {
					xhrFields: { withCredentials: true },
					...syncOptions,
					crossDomain: true,
				});
			};
		})();

		document.addEventListener('click', this.externalHrefListener.bind(this));
		InputManager.on('back', () => this.back());

		const terminationEvent = 'onpagehide' in window.self ? 'pagehide' : 'unload';
		window.addEventListener(terminationEvent, this.onTermination.bind(this));

		if (!Environment.isProd()) {
			// Keyboard shortcut for reloading an arcade and attempt cache busting
			InputManager.on('0', () => {
				// Make sure we're not using some type of text input
				if (this.inputIsFocused()) {
					return;
				}

				const currentUrl = new URL(window.location.href);
				currentUrl.searchParams.set('cacheBuster', Date.now().toString());
				window.location.href = currentUrl.toString();
			});
		}
	}

	inputIsFocused() {
		logger.log(`[${this.inputIsFocused.name}]`);

		const activeTag = document.activeElement?.tagName || '';
		return /^(input|textarea|select|option)$/i.test(activeTag);
	}

	getRunTime() {
		logger.log(`[${this.getRunTime.name}]`);

		return window.performance.now() / 1000; // Convert to seconds to keep consistent with other analytics data
	}

	/**
	 * Handler for when the page is hidden or unloaded, depending on available triggers.
	 * @link https://developer.chrome.com/blog/page-lifecycle-api/#the-unload-event
	 * @param {Event | PageTransitionEvent} event Event from the document
	 */
	onTermination(event) {
		logger.log(`[${this.onTermination.name}]`, { event });

		Analytics.sendEvent('ArcadeTermination', {
			arcadeRunTime: this.getRunTime(),
			terminationType: event.type,
		});
	}

	externalHrefListener(event) {
		logger.log(`[${this.externalHrefListener.name}]`, { event });

		const linkEl = getAnchorInDescendentTree(event.target);
		const url = linkEl?.getAttribute('href');
		if (url && !url.startsWith('#')) {
			event.preventDefault();
			const useNewWindow = !Environment.isSetTopBox();
			const targetWindow = useNewWindow ? window.open('', '_blank') : window;

			Analytics.sendEvent('ExternalLink', { url }).finally(() => {
				targetWindow.location = url;
			});
		}
	}

	registerRoute(route, id, engineModule, pageData) {
		logger.log(`[${this.registerRoute.name}]`, { route, id, pageData, engineModule });

		if (engineModule || GameFactory.supportsEngine(pageData.type)) {
			this.route(route, id, (...params) => {
				logger.debug('[onRoute]', { id, route, pageData, engineModule });
				const { name, rewards } = pageData;

				// Initialize new options object for module
				const options = { route, ...pageData };

				// Map the route parameters to options for the View.
				const parameters = route.match(/[:*]\w+/g);
				const routeParams = params;
				if (parameters) {
					parameters.forEach((paramName, index) => {
						options[paramName.substring(1)] = routeParams[index];
					});
				}

				/**
				 * Manually send page views as described here:
				 * https://developers.google.com/analytics/devguides/collection/gtagjs/pages?hl=en#manual_pageviews
				 */
				Analytics.sendEvent('page_view', {
					page_title: id,
					page_location: window.location.href,
					page_path: window.location.pathname + window.location.hash,
					rewards,
				});

				Promise.resolve(engineModule || GameFactory.getEngine(pageData.type)).then(
					(Engine) => {
						if (Engine) {
							this.setCurrentPage(() => new Engine(options), name);
						} else {
							logger.error('[Failed to load engine]', {
								engineModule,
								Engine,
								pageData,
							});
						}
					},
				);
			});
		} else {
			logger.debug('[No engine available]', { pageData });
		}
	}

	setCurrentPage(page, name) {
		logger.log(`[${this.setCurrentPage.name}]`, { name, page });

		if (page) {
			const container = $(this.selector);
			const previousPage = this.currentPage;

			gsap.timeline()
				.fadeOut(container.get(0))
				.add(() => {
					// Create new page
					const newPage = page();
					const pageId = newPage.id || newPage.options.id || '';

					this.currentPage = newPage;
					newPage.$el.attr('id', 'page').attr('arcade-page', pageId).fadeTo(0, 0);

					// Replace with newest page and display
					container.replaceWith(newPage.el);
					this.trigger('pageMounted', pageId);
					gsap.timeline().fadeIn(newPage.el);

					// Update window title and scroll
					$('title').text(name);
					$(window).scrollTop(0);

					// Clean up old page
					previousPage?.destroy?.();
				});
		}
	}

	navigate(fragment, options) {
		logger.log(`[${this.navigate.name}]`, { fragment, options });

		const hash = window.location.hash || '#home';
		this.previousHash = hash.substring(1);

		return super.navigate(fragment, { trigger: true, ...options });
	}

	back() {
		logger.log(`[${this.back.name}]`);

		if (this.backStack.length && typeof this.backStack[0] === 'function') {
			this.backStack[0]();
			return;
		}

		if (PopUpManager.isDisplayed || this.inputIsFocused()) {
			logger.debug('Skipping back navigation', {
				popUpIsDisplayed: PopUpManager.isDisplayed,
				inputIsFocused: this.inputIsFocused(),
			});
			return;
		}

		if (this.isAtHome()) {
			this.exit();
		} else {
			// Just force the user back to the home page
			this.navigate('#home', {
				trigger: true,
			});
		}
	}

	/**
	 * Overrides the default back navigation behavior of the router. Multiple calls to overrideBackNavigation
	 * will be added onto a stack with the most recent taking precedence.
	 *
	 * @param {function} overrideFunction - The function to be executed when the back button is pressed.
	 */
	overrideBackNavigation(overrideFunction) {
		logger.log(`[${this.overrideBackNavigation.name}]`, { overrideFunction });

		this.backStack.unshift(overrideFunction);
	}

	/**
	 * Removes a previously set back navigation override function from the back stack.
	 *
	 * @param {function} overrideFunction - A reference to the original override function to be removed from the back handling.
	 */
	removeBackOverride(overrideFunction) {
		logger.log(`[${this.removeBackOverride.name}]`);

		this.backStack = this.backStack.filter((func) => func !== overrideFunction);
	}

	exit() {
		logger.log(`[${this.exit.name}]`);

		if (Environment.isInHub()) {
			// Ask hub to close this arcade and return to hub view
			window.parent.postMessage(HUB_EVENTS.returnToHub, document.referrer);
			AudioManager.stopAllAudio();
			return;
		}

		const isSetTopBox = Environment.isSetTopBox();
		const isVerizon = Environment.isVerizonSetTopBox();
		if (!isSetTopBox || isVerizon) {
			// No way to close the arcade. Ignore exit request.
			logger.info('No way to exit the arcade');
			return;
		}

		this.promptExitConfirmation()
			.then(
				() => {
					// User confirmed exit
					Analytics.sendEvent('ArcadeExit', {
						status: 'confirmed',
						arcadeRunTime: this.getRunTime(),
					}).finally(() => {
						AudioManager.stopAllAudio();

						// Environment dependant shutdown calls
						if (Environment.isComcastSetTopBox()) {
							Comcast.shutdown();
						} else if (Environment.isCordova()) {
							Android.exitApp();
						} else if (Environment.isTizen()) {
							// Tizen applications pass message out of iframe wrapper to trigger native close functionality
							window.parent.postMessage('exit', '*');
						} else {
							window.close();
						}
					});
				},
				() => {
					// User cancled exit. Return focus to application.
					Analytics.sendEvent('ArcadeExit', { status: 'Cancelled' });
					InputManager.focus();
				},
			)
			.finally(() => PopUpManager.dismissDialog());
	}

	promptExitConfirmation() {
		logger.log(`[${this.promptExitConfirmation.name}]`);

		return new Promise((resolve, reject) => {
			PopUpManager.displayDialog({
				message: 'Are you sure you want to exit?',
				btns: [
					{ text: ['Yes'], className: 'btn-confirm' },
					{ text: ['No'], className: 'btn-close' },
				],
				events: {
					'click .btn-confirm': resolve,
					'click .btn-close': reject,
				}
			});
		});
	}

	isAtHome() {
		logger.log(`[${this.isAtHome.name}]`);

		const isAtHome =
			_.isEmpty(window.location.hash) ||
			window.location.hash.toLowerCase().replace('#', '') === 'home';
		logger.debug({ isAtHome });

		return isAtHome;
	}
}

export default new Router();
